import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
} from '@mui/material';
import {messages} from '@smartmixin/i18n';
import {ChevronRight} from 'mdi-material-ui';
import React from 'react';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';

type BreadcrumbItem = {
  label: string;
  path?: string;
}

type AppBreadcrumbsProps = {
  stationName?: string;
  customItems?: BreadcrumbItem[];
}

const AppBreadcrumbs: React.FC<AppBreadcrumbsProps> = ({
  stationName,
  customItems,
}) => {
  const {formatMessage} = useIntl();
  const {pathname} = useLocation();

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) return customItems;

    const items: BreadcrumbItem[] = [];

    // Parse pathname to build breadcrumbs
    if (pathname.startsWith('/app/stations')) {
      items.push({
        label: formatMessage(messages.common.weatherStations),
        path: '/app/stations/',
      });

      if (pathname.includes('/manage')) {
        items.push({
          label: formatMessage(messages.common.modifyFavoriteStations),
        });
      } else if (pathname.includes('/search')) {
        items.push({
          label: formatMessage(messages.common.searchForLocation),
        });
      } else {
        // Check for station detail pages
        const stationMatch = pathname.match(/\/app\/stations\/(\d+)/);
        if (stationMatch) {
          if (stationName) {
            items.push({
              label: stationName,
              path: `/app/stations/${stationMatch[1]}`,
            });
          }

          if (pathname.includes('/history')) {
            items.push({
              label: 'History',
            });
          } else if (pathname.includes('/forecast')) {
            items.push({
              label: formatMessage(messages.common.forecast),
            });
          } else if (pathname.includes('/analysis')) {
            items.push({
              label: formatMessage(messages.common.analysis),
            });
          }
        }
      }
    } else if (pathname.startsWith('/app/settings')) {
      items.push({
        label: formatMessage(messages.settings.settings),
        path: '/app/settings/',
      });

      if (pathname.includes('/services')) {
        items.push({
          label: 'Services',
        });
      } else if (pathname.includes('/profile')) {
        items.push({
          label: formatMessage(messages.settings.profile),
        });
      } else if (pathname.includes('/units')) {
        items.push({
          label: formatMessage(messages.settings.units),
        });
      }
    } else if (pathname.startsWith('/help')) {
      items.push({
        label: formatMessage(messages.common.help),
        path: '/help',
      });
    } else if (pathname.startsWith('/map')) {
      items.push({
        label: formatMessage(messages.common.weatherMap),
      });
    }

    return items;
  };

  const breadcrumbs = getBreadcrumbs();

  if (breadcrumbs.length === 0) return null;

  return (
    <MuiBreadcrumbs
      separator={<ChevronRight sx={{fontSize: 16}} />}
      sx={{
        mb: 2,
        '& .MuiBreadcrumbs-ol': {
          flexWrap: 'nowrap',
        },
        '& .MuiBreadcrumbs-li': {
          whiteSpace: 'nowrap',
        },
      }}>
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;

        if (isLast || !item.path) {
          return (
            <Typography
              key={index}
              color="text.primary"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={index}
            href={item.path}
            color="text.secondary"
            underline="hover"
            sx={{
              fontSize: '0.875rem',
              '&:hover': {
                color: 'primary.main',
              },
            }}>
            {item.label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};

export default AppBreadcrumbs;
